import React, { useMemo, useState } from 'react';
import { computeBetStats, formatMoney } from '../../utils/betStats';
import './BetsBreakdown.css';

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const MIXED = 'mista';

// A bet belongs to a sport when all its games are that sport; otherwise "Mista"
function sportOf(bet) {
  const sports = new Set(bet.games.map(g => g.sport));
  return sports.size === 1 ? [...sports][0] : MIXED;
}

function monthOf(bet) {
  const match = /^(\d{4})-(\d{2})/.exec(String(bet.date || ''));
  return match ? `${match[1]}-${match[2]}` : null;
}

function groupStats(bets, keyOf) {
  const groups = new Map();
  bets.forEach(bet => {
    const key = keyOf(bet);
    if (!key) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(bet);
  });
  return [...groups.entries()].map(([key, list]) => ({ key, ...computeBetStats(list) }));
}

function signedPct(value) {
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
}

function BreakdownTable({ rows, label, renderLabel }) {
  const maxAbs = Math.max(1, ...rows.map(r => Math.abs(r.profit)));
  return (
    <div className="breakdown-table-wrap">
      <table className="breakdown-table">
        <thead>
          <tr>
            <th>{label}</th>
            <th className="num-col">Apostas</th>
            <th className="num-col">G / P</th>
            <th className="num-col hide-sm">Win rate</th>
            <th className="num-col hide-sm">Apostado</th>
            <th className="num-col">Profit</th>
            <th className="num-col hide-sm">ROI</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const tone = row.profit > 0 ? 'pos' : row.profit < 0 ? 'neg' : '';
            return (
              <tr key={row.key}>
                <td className="breakdown-label">{renderLabel(row.key)}</td>
                <td className="num-col">{row.totalBets}</td>
                <td className="num-col">
                  <span className="rec-won">{row.wonBets}</span>
                  <span className="rec-sep"> / </span>
                  <span className="rec-lost">{row.lostBets}</span>
                </td>
                <td className="num-col hide-sm">
                  {row.wonBets + row.lostBets > 0 ? `${row.winRate.toFixed(0)}%` : '—'}
                </td>
                <td className="num-col hide-sm">{formatMoney(row.totalStake)}</td>
                <td className={`num-col breakdown-profit tone-${tone}`}>
                  <span className="breakdown-bar" aria-hidden="true">
                    <span style={{ width: `${(Math.abs(row.profit) / maxAbs) * 100}%` }} />
                  </span>
                  {formatMoney(row.profit, { sign: true })}
                </td>
                <td className={`num-col hide-sm tone-${tone}`}>{signedPct(row.roi)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Performance per sport and per month for the (filtered) bets on the page
function BetsBreakdown({ bets, sportNames, sportIcons, sportColors }) {
  const [tab, setTab] = useState('sport');

  const bySport = useMemo(
    () => groupStats(bets, sportOf).sort((a, b) => b.totalBets - a.totalBets),
    [bets]
  );
  const byMonth = useMemo(
    () => groupStats(bets, monthOf).sort((a, b) => b.key.localeCompare(a.key)),
    [bets]
  );

  if (!bets.length) return null;

  const renderSport = (key) => {
    if (key === MIXED) {
      return <span className="breakdown-sport"><span className="breakdown-sport-icon mixed" />Mistas</span>;
    }
    const Icon = sportIcons[key];
    return (
      <span className="breakdown-sport">
        <span className="breakdown-sport-icon" style={{ color: sportColors[key] }}>{Icon && <Icon />}</span>
        {sportNames[key] || key}
      </span>
    );
  };

  const renderMonth = (key) => {
    const [year, month] = key.split('-');
    return `${MONTHS[Number(month) - 1]} ${year}`;
  };

  return (
    <section className="bets-breakdown" aria-label="Desempenho">
      <div className="breakdown-head">
        <h2 className="breakdown-title">Desempenho</h2>
        <div className="breakdown-tabs" role="tablist">
          <button role="tab" aria-selected={tab === 'sport'} className={tab === 'sport' ? 'active' : ''} onClick={() => setTab('sport')}>
            Por desporto
          </button>
          <button role="tab" aria-selected={tab === 'month'} className={tab === 'month' ? 'active' : ''} onClick={() => setTab('month')}>
            Por mês
          </button>
        </div>
      </div>

      {tab === 'sport'
        ? <BreakdownTable rows={bySport} label="Desporto" renderLabel={renderSport} />
        : <BreakdownTable rows={byMonth} label="Mês" renderLabel={renderMonth} />}

      {tab === 'sport' && bySport.some(r => r.key === MIXED) && (
        <p className="breakdown-note">Mistas: múltiplas com jogos de desportos diferentes.</p>
      )}
    </section>
  );
}

export default BetsBreakdown;
